import { socialImageMeta } from "@/lib/social-sharing";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { getAllPosts } from "@/lib/posts.functions";
import { listApprovedThemes } from "@/lib/themes-public.functions";
import { themeBySlug } from "@/lib/themes-public";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { ThemeConstellation } from "@/components/site/ThemeConstellation";
import { BookCard, IdeasBetween, PostRows, SectionTitle, pickIdeasBetween } from "@/components/site/blocks";
import { THEME_BOOK, THEME_DOT } from "@/components/site/theme-ui";

const SITE = "https://akillisurdurulebilirlik.com";

export const Route = createFileRoute("/tema/$slug")({
  loader: async ({ params }) => {
    const theme = themeBySlug(params.slug);
    if (!theme) throw notFound();
    const [posts, themes] = await Promise.all([getAllPosts({ data: { lang: "tr" } }), listApprovedThemes()]);
    const map = new Map(themes.map((t) => [t.slug, t]));
    const primary = posts.filter((p) => map.get(p.slug)?.primary === theme.key);
    const secondary = posts
      .filter((p) => map.get(p.slug)?.secondary === theme.key)
      .map((p) => ({ ...p, theme: map.get(p.slug)!.primary }));
    const between = pickIdeasBetween(posts, map, theme.key, new Set(primary.slice(0, 4).map((p) => p.slug)));
    return { theme, primary, secondary, between };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Tema bulunamadı" }, { name: "robots", content: "noindex" }] };
    const { theme } = loaderData;
    const title = `${theme.title} — Akıllı Sürdürülebilirlik`;
    const url = `${SITE}/tema/${theme.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: `${theme.title} temasındaki yazılar. ${theme.question}` },
        { property: "og:title", content: title },
        { property: "og:description", content: theme.question },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: theme.question },
        ...socialImageMeta(),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: theme.title,
            description: theme.question,
            url,
            inLanguage: "tr-TR",
            mainEntity: {
              "@type": "ItemList",
              itemListElement: loaderData.primary.map((p, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `${SITE}/yazilar/${p.slug}`,
                name: p.title,
              })),
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: theme.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: ThemeNotFound,
  component: ThemePage,
});

function ThemeNotFound() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-20 text-center">
      <h1 className="font-serif text-3xl">Tema bulunamadı</h1>
      <Link to="/" className="mt-6 inline-block text-clay underline">Ana sayfaya dön</Link>
    </div>
  );
}

function ThemePage() {
  const { theme, primary, secondary, between } = Route.useLoaderData();
  const latest = primary.slice(0, 4);
  const rest = primary.slice(4);
  const book = THEME_BOOK[theme.key];
  const [l1, ...lr] = theme.title.split(" ");
  return (
    <>
      <section className="mx-auto max-w-7xl px-5 pt-5 sm:px-8 lg:pt-7">
        <Breadcrumbs items={[{ label: "Fikirlerin İzinde", to: "/" }, { label: theme.title }]} />
        <div className="mt-5 grid gap-6 pb-8 lg:mt-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-10 lg:pb-12">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-ink/80">
              <span aria-hidden className={`size-2.5 rounded-full ${THEME_DOT[theme.key]}`} /> Fikirlerin İzinde
            </p>
            <h1 className="mt-3 font-serif text-[44px] font-medium leading-[0.95] sm:text-[64px] lg:text-[76px]">
              {l1}<br className="hidden lg:block" /> {lr.join(" ")}
            </h1>
            <span aria-hidden className={`mt-5 block h-1 w-12 ${THEME_DOT[theme.key]}`} />
            <p className="mt-5 max-w-md font-serif text-[20px] leading-snug text-ink/80 sm:text-[24px] lg:text-[26px]">{theme.question}</p>
          </div>
          <div className="mx-auto w-full max-w-[34rem] lg:max-w-none">
            <ThemeConstellation selected={theme.key} />
          </div>
        </div>
      </section>

      <section aria-labelledby="son-yazilar" className="border-t border-ink/10">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 py-8 sm:px-8 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:gap-12 lg:py-12">
          <div className="min-w-0">
            <SectionTitle
              id="son-yazilar"
              title="Bu alandaki son yazılar"
              link={primary.length > 4 ? (
                <a href="#tum-yazilar" className="inline-flex min-h-11 items-center gap-2 font-serif text-sm text-theme-tech hover:underline">
                  Bu alandaki tüm yazılar <ArrowRight aria-hidden className="size-4" />
                </a>
              ) : null}
            />
            <div className="mt-3 lg:mt-5">
              {latest.length ? <PostRows posts={latest} /> : <p className="py-6 text-ink/70">Bu temada henüz onaylı yazı yok.</p>}
            </div>
          </div>
          <BookCard book={book} />
        </div>
      </section>

      <IdeasBetween posts={between} intro={`${theme.title}, diğer düşünce alanlarıyla da yakından ilişkili. Aynı sorular, farklı açılardan karşımıza çıkıyor.`} />

      {(rest.length > 0 || secondary.length > 0) && (
        <section id="tum-yazilar" aria-labelledby="tum-baslik" className="scroll-mt-6 border-t border-ink/10">
          <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8 lg:py-12">
            <SectionTitle id="tum-baslik" title={`Bu alandaki tüm yazılar · ${primary.length}`} />
            <div className="mt-3 grid gap-x-12 lg:grid-cols-2">
              <PostRows posts={rest} />
            </div>
            {secondary.length > 0 && (
              <div className="mt-10">
                <h3 className="font-serif text-xl font-medium">Bu temaya da değinen yazılar · {secondary.length}</h3>
                <div className="mt-3 grid gap-x-12 lg:grid-cols-2">
                  <PostRows posts={secondary} showTheme />
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
}
